import React, { useCallback, useEffect, useRef, useState } from "react";

const Carousel = ({
  children,
  showBreadCrumb,
}: {
  children: any;
  showBreadCrumb: boolean;
}) => {
  const carouselRef = useRef<HTMLDivElement | null>(null);
  const autoplayRef = useRef<NodeJS.Timeout | null>(null);
  const slideRef = useRef<HTMLDivElement | null>(null);
  const slideContainerRef = useRef<HTMLDivElement | null>(null);
  const [activeIndex, setActiveIndex] = useState(0);

  // If a children is null, it will not count it as a child
  const childrenToRender = React.Children.map(children, (child) => {
    if (child == null) return null;
    return child;
  }).filter(Boolean);

  let slideWidth = 390;
  if (slideRef.current) {
    slideWidth = slideRef.current.offsetWidth;
  }

  const handleScroll = useCallback(() => {
    if (slideContainerRef.current) {
      const scrollLeft = slideContainerRef.current.scrollLeft;
      setActiveIndex(Math.floor(scrollLeft / slideWidth));
    }
  }, [slideWidth]);

  useEffect(() => {
    if (slideContainerRef.current) {
      slideContainerRef.current.addEventListener("scroll", handleScroll);
    }

    return () => {
      if (slideContainerRef.current) {
        slideContainerRef.current.removeEventListener("scroll", handleScroll);
      }
    };
  }, [slideWidth]);
  useEffect(() => {
    if (!carouselRef.current || !slideContainerRef.current) {
      return;
    }

    const navigate = (arg: string | number) => {
      if (!slideContainerRef.current) return;
      const newScrollPosition = getNewScrollPosition(arg);
      setActiveIndex(Math.floor(newScrollPosition / slideWidth));
      slideContainerRef.current.scrollLeft = newScrollPosition;
    };

    const getNewScrollPosition = (arg: string | number) => {
      if (!slideContainerRef.current) return 0;

      const maxScrollLeft = slideContainerRef.current.scrollWidth - slideWidth;

      if (arg === "forward") {
        const x = slideContainerRef.current.scrollLeft + slideWidth;
        return x <= maxScrollLeft + slideWidth / childrenToRender.length
          ? x
          : 0;
      } else if (arg === "backward") {
        const x = slideContainerRef.current.scrollLeft - slideWidth;
        return x >= 0 ? x : maxScrollLeft;
      } else if (typeof arg === "number") {
        return arg * slideWidth;
      }

      return 0;
    };

    const handleIndicatorClick = (index: number) => {
      clearInterval(autoplayRef.current!);
      navigate(index);
    };

    document.querySelectorAll(".slide-indicator").forEach((dot, index) => {
      dot.addEventListener("click", () => handleIndicatorClick(index));
    });

    autoplayRef.current = setInterval(() => navigate("forward"), 5000);
    const restartInterval = () => {
      clearInterval(autoplayRef.current!);
      autoplayRef.current = setInterval(() => navigate("forward"), 5000);
    };

    const handleResize = () => {
      if (slideRef.current) {
        slideWidth = slideRef.current.offsetWidth;
      }
    };

    window.addEventListener("resize", handleResize);
    if (slideContainerRef.current) {
      slideContainerRef.current.addEventListener("mouseenter", () =>
        clearInterval(autoplayRef.current!)
      );
      slideContainerRef.current.addEventListener("mouseleave", () =>
        restartInterval()
      );
      slideContainerRef.current.addEventListener("touchstart", () =>
        clearInterval(autoplayRef.current!)
      );
      slideContainerRef.current.addEventListener("touchend", () =>
        restartInterval()
      );
    }

    return () => {
      clearInterval(autoplayRef.current!);
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  return (
    <div className="relative mx-auto flex max-w-7xl flex-col" ref={carouselRef}>
      <div
        id="slide-container"
        className="flex h-full snap-x snap-mandatory items-center overflow-y-hidden overflow-x-scroll scroll-smooth"
        ref={slideContainerRef}
      >
        {React.Children.map(childrenToRender, (child, index) => {
          if (index === 0 && React.isValidElement(child)) {
            return React.cloneElement(child as React.ReactElement, {
              ref: slideRef,
            });
          }
          return child;
        })}
      </div>
      {showBreadCrumb && (
        <div className="absolute bottom-0 left-0 right-0 flex items-center justify-center">
          {React.Children.map(childrenToRender, (_, index) => (
            <div
              key={index}
              className={
                "slide-indicator" + (activeIndex === index ? " active" : "")
              }
            ></div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Carousel;
